import React, { useState } from "react";
import UserIcon from '@mui/icons-material/Person';
import HandIcon from '@mui/icons-material/PanTool';
import LogoutIcon from '@mui/icons-material/Logout';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SettingsIcon from '@mui/icons-material/Settings';
import { ReactComponent as AceLogo } from '../../assets/Ace_Hardware_Logo.svg';
import { MuiButton } from '@ace/ace-ui-web';
import { Box, Drawer, IconButton, Typography } from '@mui/material';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"
import styles from './sidebar.module.css';
import PostShift from "../post-shift";
import GigRequest from "../gig-request";
import { useUserRole } from '../../hooks/useUserRole';
import { useIsGigWorker } from '../../hooks/useIsGigWorker';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { logoutUser } from '../../redux/AuthReducer/authSlice';

interface SideBarProps {
    isExpanded: boolean;
    onToggle: () => void;
    userName?: string;
}

const MOBILE_BREAKPOINT = 768;

interface SidebarDrawerProps {
    open: boolean;
    title: string;
    subtitle: string;
    footerContent?: React.ReactNode | null;
    onClose: () => void;
    children: React.ReactNode;
}

const SidebarDrawer: React.FC<SidebarDrawerProps> = ({ open, title, subtitle, footerContent, onClose, children }) => {
    return (
        <Drawer
            open={open}
            anchor="right"
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 'min(720px, 100%)' },
                    maxWidth: '100vw'
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, borderBottom: '1px solid #e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {subtitle}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} aria-label="Close drawer">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {children}
                </Box>
                {footerContent && (
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2,
                            borderTop: '1px solid #e0e0e0'
                        }}
                    >
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                            {footerContent}
                        </Box>
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

const SideBar: React.FC<SideBarProps> = ({ isExpanded, onToggle, userName }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isPostShiftDrawerOpen, setIsPostShiftDrawerOpen] = useState(false)
    const [isGigRequestDrawerOpen, setIsGigRequestDrawerOpen] = useState(false)
    const [postShiftFooterContent, setPostShiftFooterContent] = useState<React.ReactNode | null>(null);
    const [gigRequestFooterContent, setGigRequestFooterContent] = useState<React.ReactNode | null>(null);

    const { user: currentUser } = useAppSelector((state) => state.currentUser);

    // Get user role using the hook
    const { isHROrManager, isSuperUser } = useUserRole();

    // Check if user is a GIG worker
    const isGigWorker = useIsGigWorker();

    const buttonStyles = {
        border: 'none',
        outline: 'none',
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '24px',
        '&:focus': {
            outline: 'none',
        },
        '&:active': {
            outline: 'none',
        },
        '& svg': {
            marginRight: '24px',
        },
        '& .Icon': {
            marginRight: '24px',
        },
    };

    const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;

    const handleMenuItemClick = (path: string) => {
        navigate(path);
        // Auto-close sidebar on mobile after navigation
        if (isMobile() && isExpanded) {
            onToggle();
        }
    };

    const handlePostShiftClick = () => {
        setIsPostShiftDrawerOpen(true);
        // Auto-close sidebar on mobile
        if (isMobile() && isExpanded) {
            onToggle();
        }
    }

    const handleGigRequestClick = () => {
        setIsGigRequestDrawerOpen(true);
        // Auto-close sidebar on mobile
        if (isMobile() && isExpanded) {
            onToggle();
        }
    }

    const handleCloseDrawer = () => {
        setIsPostShiftDrawerOpen(false);
        setIsGigRequestDrawerOpen(false);
        setPostShiftFooterContent(null);
        setGigRequestFooterContent(null);
    }

    const handleLogout = async () => {
        await dispatch(logoutUser()).unwrap();
        navigate('/login');
    };

    return (
        <>
            {/* Backdrop — tapping it closes the sidebar on mobile */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        className={styles.backdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onToggle}
                        aria-hidden="true"
                    />
                )}
            </AnimatePresence>

            <div className={`${styles.sidebar} ${isExpanded ? styles.expanded : styles.collapsed}`}>
                <div className={styles.sidebarLogo}>
                    <AceLogo onClick={onToggle} />
                    {isExpanded && <span className={styles.logoSubtitle}>MyAceFlex</span>}
                    {isExpanded && isSuperUser && currentUser?.siteCd && (
                        <div className={styles.siteBadge}>
                            <LocationOnIcon className={styles.siteBadgeIcon} />
                            <span className={styles.siteBadgeText}>{currentUser.siteCd}</span>
                        </div>
                    )}
                </div>

                {isExpanded && userName && (
                    <div className={styles.userCard}>
                        <UserIcon className={styles.userIcon} />
                        <span className={styles.userName}>{userName}</span>
                    </div>
                )}

                {isExpanded && (
                    <nav className={styles.sidebarMenu}>
                        {/* MY SHIFTS - shows for everyone, different default routes */}
                        <MuiButton
                            colors="tertiary"
                            variant="text"
                            onClick={() => handleMenuItemClick(isHROrManager ? '/approve-reject' : '/my-shifts')}
                            sx={buttonStyles}
                        >
                            <div className={styles.menuButton}>
                                <CalendarIcon />
                                MY SHIFTS
                            </div>
                        </MuiButton>

                        {/* GRAB SHIFT - only for regular users */}
                        {!isHROrManager && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={() => handleMenuItemClick('/grab-shift')}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <HandIcon />
                                    GRAB SHIFT
                                </div>
                            </MuiButton>
                        )}

                        {/* EMPLOYEE PROFILE - only for HR/Manager */}
                        {isHROrManager && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={() => handleMenuItemClick('/employee-profile')}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <ManageAccountsIcon />
                                    EMPLOYEE PROFILE
                                </div>
                            </MuiButton>
                        )}

                        {/* MY PROFILE - shows for everyone */}
                        <MuiButton
                            colors="tertiary"
                            variant="text"
                            onClick={() => handleMenuItemClick(isHROrManager ? '/hr-profile' : '/profile')}
                            sx={buttonStyles}
                        >
                            <div className={styles.menuButton}>
                                <UserIcon />
                                MY PROFILE
                            </div>
                        </MuiButton>
                  
                        {/* REPORTS - only for HR/Manager */}
                        {isHROrManager && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={() => handleMenuItemClick('/reports')}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <BarChartIcon />
                                    REPORTS
                                </div>
                            </MuiButton>
                        )}

                        {/* REQUEST TIME - only for GIG workers */}
                        {isGigWorker && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={handleGigRequestClick}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <AddCircleOutline />
                                    REQUEST TIME
                                </div>
                            </MuiButton>
                        )}

                        {/* POST SHIFT - shows for non-GIG workers */}
                        {!isGigWorker && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={handlePostShiftClick}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <AddCircleOutline />
                                    POST SHIFT
                                </div>
                            </MuiButton>
                        )}

                        {/* SWITCH SITE - only for Super Users */}
                        {isSuperUser && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={() => handleMenuItemClick('/site-select')}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <SwapHorizIcon />
                                    SWITCH SITE
                                </div>
                            </MuiButton>
                        )}

                        {/* SETTINGS - only for HR/Manager */}
                        {isHROrManager && (
                            <MuiButton
                                colors="tertiary"
                                variant="text"
                                onClick={() => handleMenuItemClick('/settings')}
                                sx={buttonStyles}
                            >
                                <div className={styles.menuButton}>
                                    <SettingsIcon />
                                    SETTINGS
                                </div>
                            </MuiButton>
                        )}
                    </nav>
                )}

                {isExpanded && (
                    <motion.div
                        className={styles.logoutButtonContainer}
                    >
                        <MuiButton
                            colors="tertiary"
                            variant="text"
                            onClick={handleLogout}
                            sx={{ border: '2px solid var(--color-border)', borderRadius: 'calc(var(--radius) - 0.125rem)' }}
                        >
                            <div className={styles.logoutButton}>
                                <LogoutIcon />
                                <span style={{ marginLeft: '0.250rem' }}>LOGOUT</span>
                            </div>
                        </MuiButton>
                    </motion.div>
                )}
            </div>

            {/* Floating hamburger button — mobile only */}
            <motion.button
                className={styles.hamburgerBtn}
                onClick={onToggle}
                aria-label={isExpanded ? 'Close menu' : 'Open menu'}
                whileTap={{ scale: 0.9 }}
            >
                {isExpanded ? <CloseIcon /> : <MenuIcon />}
            </motion.button>

            <div>
                <SidebarDrawer
                    open={isPostShiftDrawerOpen}
                    title="Post a Shift"
                    subtitle="Create a new shift listing for others to grab"
                    footerContent={postShiftFooterContent}
                    onClose={handleCloseDrawer}
                >
                    <PostShift setFooterContent={setPostShiftFooterContent} />
                </SidebarDrawer>

                <SidebarDrawer
                    open={isGigRequestDrawerOpen}
                    title="Schedule Builder"
                    subtitle="Build your work schedule by selecting days and hours"
                    footerContent={gigRequestFooterContent}
                    onClose={handleCloseDrawer}
                >
                    <GigRequest setFooterContent={setGigRequestFooterContent} />
                </SidebarDrawer>
            </div>
        </>
    );
};

export default SideBar;